/**
 * Site requests with per-row actions (replaces the old page-wide "Edit Site Requests" mode):
 * hovering a row (or focusing it) shows a pencil — qty & due date in a small popover, Enter saves —
 * and a "⋯" menu with Open project and "Delete request…" (confirmed). Saves and deletes end in a
 * toast whose Undo reverts exactly that change (the API returns its undo id).
 * Wave 3: a check box per request for "Create LPO draft" (22 #9) — requests already on a draft / LPO say which
 * one instead ("In draft LPO-2026-004", "Ordered LPO-…") — and "Where is it?" opens the material trace (22 #10).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, CalendarDays, ClipboardList, FileText, FolderOpen, Pencil, Route, Trash2, TriangleAlert, Warehouse } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatDays, formatNumber, toDateInputValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmptyState } from '@/components/ui/empty-state';
import { RowActions } from '@/components/ui/row-actions';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { TONE_TEXT_CLASS } from '@/lib/status';
import { LinkButton, MissingText } from './SfUi';
import { groupSiteRequests } from './procurementModel';
import { orderStateLabel, selectableRequest } from './draftsModel';

const ORDER_TAG_CLASS = {
  gray: 'border-line bg-surface-2',
  amber: 'border-[color:var(--status-amber-bg)] bg-status-amber-bg',
  navy: 'border-[color:var(--status-navy-bg)] bg-status-navy-bg',
  blue: 'border-[color:var(--status-blue-bg)] bg-status-blue-bg',
};

/** "In draft LPO-2026-004" — the draft / order a request is on (22 #9). */
export function OrderStateTag({ request, className }) {
  const state = orderStateLabel(request);
  if (!state) return null;
  return (
    <span
      className={cn('inline-flex h-[20px] max-w-full items-center gap-1 whitespace-nowrap rounded-md border px-1.5 text-[11px] font-semibold', ORDER_TAG_CLASS[state.tone], TONE_TEXT_CLASS[state.tone] || 'text-ink-2', className)}
      title={request.lpo?.supplier ? `${state.text} · ${request.lpo.supplier}` : state.text}
      data-testid={`site-request-order-${request.id}`}
    >
      <FileText className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{state.label} <span className="font-mono">{state.number}</span></span>
    </span>
  );
}

/** "due 26 Sep · 4 d late" (red) / "due 03 Oct" (amber, this week) / "due 08 Oct" (muted). */
export function dueText(request) {
  if (!request.due_date) return { text: 'No due date', tone: 'amber' };
  const date = formatDate(request.due_date, { year: 'auto' });
  if (request.urgency === 'overdue') return { text: `due ${date} · ${formatDays(request.dueDays)} late`, tone: 'red' };
  if (request.urgency === 'this_week') return { text: request.dueDays === 0 ? 'due today' : `due ${date}`, tone: 'amber' };
  return { text: `due ${date}`, tone: null };
}

const undoAction = (undoId, after) => (undoId ? async () => {
  await api.post(`/undo/entries/${encodeURIComponent(undoId)}`);
  await after?.();
} : undefined);

function EditRequestForm({ request, onDone, onCancel }) {
  const fulfilled = Number(request.fulfilled_qty || 0);
  const [qty, setQty] = useState(String(request.remaining ?? request.remaining_qty ?? ''));
  const [due, setDue] = useState(toDateInputValue(request.due_date));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const qtyRef = useRef(null);
  useEffect(() => { qtyRef.current?.focus(); qtyRef.current?.select?.(); }, []);

  const submit = async (event) => {
    event.preventDefault();
    const remaining = Number(qty);
    if (!Number.isFinite(remaining) || remaining <= 0) { setError('Qty still needed must be more than 0'); return; }
    if (!due) { setError('Pick a due date'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await api.put(`/site-requests/${request.id}`, { requested_qty: fulfilled + remaining, due_date: due });
      onDone(res.data || {});
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the request'));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`site-request-edit-${request.id}`}>
      <div>
        <p className="font-heading text-[13.5px] font-bold text-ink">Edit request</p>
        <p className="mt-0.5 truncate text-[12px] text-ink-3"><span className="font-mono">{request.part_number}</span> · {request.project_name || 'Unknown project'}</p>
      </div>
      <div className="grid grid-cols-[1fr_1.4fr] gap-2">
        <div className="space-y-1">
          <Label htmlFor={`sr-qty-${request.id}`} className="text-[12px] font-semibold text-ink-2">Qty still needed</Label>
          <Input id={`sr-qty-${request.id}`} ref={qtyRef} type="number" min="0" step="any" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 text-right tabular-nums" />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`sr-due-${request.id}`} className="text-[12px] font-semibold text-ink-2">Due date</Label>
          <Input id={`sr-due-${request.id}`} type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9" />
        </div>
      </div>
      {fulfilled > 0 ? <p className="text-[11.5px] text-ink-3">{formatNumber(fulfilled)} {request.unit} already delivered — they stay on the request.</p> : null}
      {error ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving} data-testid={`site-request-save-${request.id}`}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </form>
  );
}

export function SiteRequestRow({ request, canEdit, onChanged, showProject = true, testPrefix = '', selection = null, onTrace }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const due = dueText(request);
  const qtyLabel = `${formatNumber(request.remaining)} ${request.unit}`;
  const canSelect = Boolean(selection) && selectableRequest(request);
  const selected = canSelect && selection.selectedIds.has(request.id);

  const remove = async () => {
    let undoId = '';
    const ok = await confirm({
      title: `Delete the request for ${request.part_number}?`,
      subtitle: `${qtyLabel} · ${request.project_name || 'Unknown project'} · ${request.due_date ? `due ${formatDate(request.due_date)}` : 'no due date'}`,
      description: 'The site team will no longer see this request in the queue. Deliveries already recorded are not affected.',
      tone: 'danger',
      confirmLabel: 'Delete request',
      onConfirm: async () => {
        const res = await api.delete(`/site-requests/${request.id}`);
        undoId = res.data?.undo_id || '';
      },
    });
    if (!ok) return;
    notify.success(`Request for ${request.part_number} deleted`, { undo: undoAction(undoId, onChanged) });
    await onChanged?.();
  };

  const saved = async (data) => {
    setEditing(false);
    notify.success(`Request for ${request.part_number} updated`, { undo: undoAction(data.undo_id, onChanged) });
    await onChanged?.();
  };

  return (
    <li
      className={cn(
        'group relative grid items-center gap-3 px-4 py-2.5 transition-colors hover:bg-surface-hover focus-within:bg-surface-hover sm:px-[18px]',
        selection ? 'grid-cols-[18px_minmax(0,1fr)_auto] gap-x-2.5' : 'grid-cols-[minmax(0,1fr)_auto]',
        editing && 'bg-surface-hover',
        selected && 'bg-brand-soft hover:bg-brand-soft focus-within:bg-brand-soft'
      )}
      data-testid={`${testPrefix}site-request-${request.id}`}
    >
      {selection ? (
        <span className="flex items-center justify-center">
          {canSelect ? (
            <TableCheckbox
              checked={selected}
              onCheckedChange={() => selection.onToggle(request.id)}
              aria-label={`Select ${request.part_number} for an LPO draft`}
              data-testid={`${testPrefix}site-request-select-${request.id}`}
            />
          ) : <span className="sr-only">{request.lpo ? 'Already on an LPO' : 'Nothing left to order'}</span>}
        </span>
      ) : null}
      <div className="min-w-0">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-mono text-[13px] font-semibold text-ink">{request.part_number || '—'}</span>
          <span className="min-w-0 truncate text-[12px] text-ink-3">{request.description}</span>
        </div>
        <div className="mt-0.5 truncate text-[12px] text-ink-3">
          {showProject ? (request.project_name ? <span>{request.project_name}</span> : <MissingText>Unknown project</MissingText>) : null}
          {showProject ? <span className="text-ink-4" aria-hidden="true"> · </span> : null}
          <span className={cn(due.tone === 'red' && 'font-semibold text-status-red-ink', due.tone === 'amber' && 'font-semibold text-status-amber-ink')}>{due.text}</span>
          {request.notes ? <span className="text-ink-3"> · {request.notes}</span> : null}
        </div>
        {request.lpo || request.source === 'inventory_shortage' ? (
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
            {request.source === 'inventory_shortage' ? (
              <span
                className="inline-flex h-[20px] items-center gap-1 whitespace-nowrap rounded-md border border-line bg-surface-2 px-1.5 text-[11px] font-semibold text-ink-2"
                title="Sent from Inventory (Send shortage / Auto-book): stock did not cover the project's BOQ"
                data-testid={`site-request-from-inventory-${request.id}`}
              >
                <Warehouse className="h-3 w-3 shrink-0" aria-hidden="true" />
                From Inventory
              </span>
            ) : null}
            {request.lpo ? <OrderStateTag request={request} /> : null}
          </div>
        ) : null}
      </div>
      <div className="relative flex min-h-[30px] items-center justify-end gap-1">
        <span className={cn('whitespace-nowrap text-[13px] font-bold tabular-nums text-ink transition-opacity', canEdit && 'group-hover:opacity-0 group-focus-within:opacity-0 [@media(hover:none)]:!opacity-100', editing && 'opacity-0')}>
          {qtyLabel}
        </span>
        {canEdit ? (
          <Popover open={editing} onOpenChange={setEditing}>
            <PopoverAnchor asChild>
              <span
                className={cn(
                  'absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100',
                  '[@media(hover:none)]:static [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100',
                  editing && 'opacity-100'
                )}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  title="Edit qty & due date"
                  aria-label={`Edit qty and due date for ${request.part_number}`}
                  onClick={() => setEditing(true)}
                  data-testid={`site-request-edit-btn-${request.id}`}
                >
                  <Pencil className="h-[15px] w-[15px]" aria-hidden="true" />
                </Button>
                <RowActions
                  label={`Actions for ${request.part_number}`}
                  data-testid={`site-request-menu-${request.id}`}
                  items={[
                    { label: 'Edit qty & due date', icon: Pencil, onSelect: () => setEditing(true) },
                    onTrace && { label: 'Where is it?', icon: Route, onSelect: () => onTrace(request), testId: `site-request-trace-${request.id}` },
                    request.project_id && { label: 'Open project', icon: FolderOpen, onSelect: () => navigate(`/projects/${request.project_id}`) },
                    { label: 'Delete request…', icon: Trash2, danger: true, onSelect: remove, testId: `site-request-delete-${request.id}` },
                  ]}
                />
              </span>
            </PopoverAnchor>
            <PopoverContent align="end" className="w-[300px] rounded-xl border-line bg-surface p-3.5 text-ink shadow-overlay">
              <EditRequestForm request={request} onDone={saved} onCancel={() => setEditing(false)} />
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    </li>
  );
}

export function SiteRequestList({ requests, canEdit, onChanged, emptyAction, showProject = true, className, testId = 'site-request-list', rowTestPrefix = '', selection = null, onTrace }) {
  if (!requests.length) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No open site requests"
        text="Engineers raise requests from a project’s Material tab."
        action={emptyAction}
        data-testid={`${testId}-empty`}
      />
    );
  }
  return (
    <ul className={cn('divide-y divide-line-2', className)} data-testid={testId}>
      {requests.map((request) => (
        <SiteRequestRow key={request.id} request={request} canEdit={canEdit} onChanged={onChanged} showProject={showProject} testPrefix={rowTestPrefix} selection={selection} onTrace={onTrace} />
      ))}
    </ul>
  );
}

export function SiteRequestTip({ onOpenProjects }) {
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 border-t border-line-2 px-4 py-2.5 text-[12px] text-ink-3 sm:px-[18px]">
      Engineers raise requests from a project’s Material tab.
      <LinkButton onClick={onOpenProjects}>Open Projects</LinkButton>
    </p>
  );
}

const GROUP_ICON = { overdue: TriangleAlert, this_week: CalendarClock, later: CalendarDays };
const GROUP_TONE = { overdue: 'text-status-red-ink', this_week: 'text-status-amber-ink', later: 'text-ink-3' };

/**
 * The queue by urgency (22 #7): OVERDUE · DUE THIS WEEK · LATER. Overdue requests never drop out —
 * they lead the list in red until they are delivered or changed.
 */
export function SiteRequestGroups({ requests, canEdit, onChanged, emptyAction, showProject = true, className, testId = 'site-request-groups', rowTestPrefix = '', selection = null, onTrace }) {
  if (!requests.length) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No open site requests"
        text="Engineers raise requests from a project’s Material tab."
        action={emptyAction}
        data-testid={`${testId}-empty`}
      />
    );
  }
  return (
    <div className={cn('pb-1', className)} data-testid={testId}>
      {groupSiteRequests(requests).map((group) => {
        const Icon = GROUP_ICON[group.key];
        return (
          <section key={group.key} aria-label={`${group.label}: ${group.items.length}`} data-testid={`${testId}-${group.key}`}>
            <h3 className={cn('flex items-center gap-1.5 px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.06em] sm:px-[18px]', GROUP_TONE[group.key])}>
              <Icon className="h-[13px] w-[13px]" aria-hidden="true" />
              {group.label} · {group.items.length}
            </h3>
            <ul className="divide-y divide-line-2">
              {group.items.map((request) => (
                <SiteRequestRow key={request.id} request={request} canEdit={canEdit} onChanged={onChanged} showProject={showProject} testPrefix={rowTestPrefix} selection={selection} onTrace={onTrace} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
